import React from 'react';
import { X, User, Calendar, ExternalLink } from 'lucide-react';
import PresignedImage from './PresignedImage';

const ArtistDetailModal = ({ artist, isOpen, onClose, user }) => {
  if (!isOpen || !artist) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'rejected':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'pending':
      default:
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

           return (
           <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[9999] p-4">
      <div className="relative bg-black/90 border border-white/20 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-[0_4px_4px_rgba(0,0,0,0.17)]">
        <div className="sticky top-0 bg-black/90 border-b border-white/20 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-white">{artist.artist_name}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
        
                       <div className="p-6">
                 <div className="space-y-6">
                                      {/* Basic Info */}
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                     <div>
                       <h3 className="text-lg font-semibold text-white mb-4">Artist Information</h3>
                       
                       {/* Artist Image */}
                       {artist.image && (
                         <div className="mb-4">
                           <div className="w-32 h-auto rounded-lg overflow-hidden border border-white/20">
                             <PresignedImage
                               assetKey={artist.image}
                               assetType="artist"
                               status={artist.status || 'pending'}
                               alt={artist.artist_name}
                               className="w-full h-full object-cover"
                             />
                           </div>
                         </div>
                       )}
                       
                                                <div className="space-y-3">
                           <div className="flex items-center space-x-3">
                             <span className="text-white">Artist Name: {artist.artist_name}</span>
                           </div>
                         {artist.artist_legal_name && (
                           <div className="flex items-center space-x-3">
                             <span className="text-white">Legal Name: {artist.artist_legal_name}</span>
                           </div>
                         )}
                         <div className="flex items-center space-x-3">
                           <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getStatusColor(artist.status)}`}>
                             {artist.status || 'pending'}
                           </span>
                         </div>
                       </div>
                     </div>
              
                             <div>
                 <h3 className="text-lg font-semibold text-white mb-4">Status & Metadata</h3>
                 <div className="space-y-3">
                   <div className="flex items-center space-x-3">
                     <Calendar className="h-4 w-4 text-gray-400" />
                     <span className="text-white">Submitted: {formatDate(artist.created_at)}</span>
                   </div>
                   {artist.updated_at && (
                     <div className="flex items-center space-x-3">
                       <Calendar className="h-4 w-4 text-gray-400" />
                       <span className="text-white">Last Updated: {formatDate(artist.updated_at)}</span>
                     </div>
                   )}
                   {user?.role === 'admin' && (
                     <div className="flex items-center space-x-3">
                       <User className="h-4 w-4 text-gray-400" />
                       <span className="text-white">User: {artist.user_email || 'Unknown User'}</span>
                     </div>
                   )}
                   {artist.batch_id && (
                     <div className="flex items-center space-x-3">
                       <span className="text-white">Batch ID: {artist.batch_id}</span>
                     </div>
                   )}
                 </div>
                 
                 {/* Social Media Links */}
                 {(artist.artist_fb_url || artist.artist_ig_url) && (
                   <div className="mt-6">
                     <h3 className="text-lg font-semibold text-white mb-4">Social Media</h3>
                     <div className="space-y-3">
                       {artist.artist_fb_url && (
                         <div className="flex items-center space-x-3">
                           <ExternalLink className="h-4 w-4 text-blue-400" />
                           <a 
                             href={artist.artist_fb_url} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="text-blue-400 hover:text-blue-300 transition-colors"
                           >
                             Facebook
                           </a>
                         </div>
                       )}
                       {artist.artist_ig_url && (
                         <div className="flex items-center space-x-3">
                           <ExternalLink className="h-4 w-4 text-pink-400" />
                           <a 
                             href={artist.artist_ig_url} 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="text-pink-400 hover:text-pink-300 transition-colors"
                           >
                             Instagram
                           </a>
                         </div>
                       )}
                     </div>
                   </div>
                 )}
               </div>
             </div>

             {/* Bio */}
             {artist.bio && (
               <div>
                 <h3 className="text-lg font-semibold text-white mb-4">Biography</h3>
                 <div className="text-gray-300 leading-relaxed">
                   {artist.bio}
                 </div>
               </div>
             )}

            {/* Notes */}
            {artist.notes && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Notes</h3>
                <div className="text-gray-300">
                  {artist.notes}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtistDetailModal;
